import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core/i18n';
import { WhatsAppConnectionStore } from '../../../company-communication/store/whatsapp-connection.store';
import { TPipe } from '../../../../shared/pipes';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { OnboardingStore } from '../../stores/onboarding.store';

@Component({
  selector: 'app-onboarding-step-whatsapp',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TPipe],
  template: `
    <div class="flex flex-col gap-5 px-1 py-2">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
          {{ 'onboarding.progress' | t: { current: 4, total: 4 } }}
        </p>
        <h2 class="mt-1 text-xl font-semibold text-[var(--text)]">
          {{ 'onboarding.whatsapp.title' | t }}
        </h2>
        <p class="mt-1 text-sm text-[var(--muted)]">
          {{ 'onboarding.whatsapp.subtitle' | t }}
        </p>
      </div>

      <ul class="flex flex-col gap-2 text-sm text-[var(--text)]">
        <li class="flex items-start gap-2">
          <lucide-icon
            name="check"
            [size]="18"
            class="mt-0.5 text-[var(--accent)]"
            aria-hidden="true"
          ></lucide-icon>
          <span>{{ 'onboarding.whatsapp.requirement1' | t }}</span>
        </li>
        <li class="flex items-start gap-2">
          <lucide-icon
            name="check"
            [size]="18"
            class="mt-0.5 text-[var(--accent)]"
            aria-hidden="true"
          ></lucide-icon>
          <span>{{ 'onboarding.whatsapp.requirement2' | t }}</span>
        </li>
      </ul>

      @if (whatsapp.errorMessage()) {
        <p class="text-sm text-[var(--danger)]">{{ whatsapp.errorMessage() }}</p>
      }

      <div class="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-xl bg-[var(--accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 hover:opacity-90"
            [disabled]="connecting()"
            (click)="connectClicked()"
          >
            @if (connecting()) {
              <lucide-icon
                name="refresh-cw"
                [size]="16"
                class="animate-spin"
                aria-hidden="true"
              ></lucide-icon>
              {{ 'onboarding.whatsapp.connecting' | t }}
            } @else {
              {{ 'onboarding.whatsapp.cta' | t }}
            }
          </button>
          <button
            type="button"
            class="text-sm text-[var(--muted)] hover:text-[var(--text)] underline-offset-2 hover:underline"
            [disabled]="connecting() || finishing()"
            (click)="skipStep()"
          >
            {{ 'onboarding.whatsapp.skip' | t }}
          </button>
        </div>
        <button
          type="button"
          class="text-xs text-[var(--muted)] hover:text-[var(--text)] underline-offset-2 hover:underline"
          [disabled]="connecting() || finishing()"
          (click)="snoozeNow()"
        >
          {{ 'onboarding.snooze' | t }}
        </button>
      </div>
    </div>
  `,
})
export class OnboardingStepWhatsappComponent {
  protected readonly whatsapp = inject(WhatsAppConnectionStore);
  private readonly store = inject(OnboardingStore);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);

  private readonly userInitiatedConnection = signal(false);
  protected readonly finishing = signal(false);

  protected readonly connecting = computed(() => this.whatsapp.uiState() === 'connecting');

  constructor() {
    void this.whatsapp.loadStatus();

    effect(() => {
      if (this.userInitiatedConnection() && this.whatsapp.uiState() === 'connected') {
        this.userInitiatedConnection.set(false);
        void this.completeAfterConnection();
      }
    });
  }

  connectClicked(): void {
    if (this.connecting()) return;
    this.userInitiatedConnection.set(true);
    void this.whatsapp.startConnection();
  }

  async skipStep(): Promise<void> {
    if (this.finishing()) return;
    this.finishing.set(true);
    try {
      await this.store.advance(4);
    } finally {
      this.finishing.set(false);
    }
  }

  async snoozeNow(): Promise<void> {
    if (this.finishing()) return;
    await this.store.snooze();
  }

  private async completeAfterConnection(): Promise<void> {
    if (this.finishing()) return;
    this.finishing.set(true);
    try {
      this.toast.success(this.i18n.t('onboarding.whatsapp.toastconnected'));
      await this.store.advance(4);
    } finally {
      this.finishing.set(false);
    }
  }
}
